/**
 * Human-readable labels for the domain enums.
 *
 * These live beside the enums rather than in the web app so that a new enum
 * member cannot ship without a label — the `Record<Enum, string>` types below
 * make an omission a type error, in either app.
 *
 * Plain English (Q12), the words an operator scans a table for.
 */
import type { ActivityKind } from './dashboard.js';
import type {
  CallReason,
  CallResolution,
  CallStatus,
  ExportFormat,
  NotificationSeverity,
} from './enums.js';

export const callStatusLabels: Record<CallStatus, string> = {
  IN_PROGRESS: 'In progress',
  COMPLETED: 'Completed',
  ABANDONED: 'Abandoned',
  FAILED: 'Failed',
};

export const callReasonLabels: Record<CallReason, string> = {
  INFORMATION: 'Information',
  BOOKING: 'Booking',
  CHANGE_OR_CANCEL: 'Change or cancel',
  COMPLAINT: 'Complaint',
  SUPPORT: 'Support',
  OTHER: 'Other',
  UNKNOWN: 'Unclassified',
};

export const callResolutionLabels: Record<CallResolution, string> = {
  RESOLVED: 'Resolved',
  UNRESOLVED: 'Unresolved',
  ESCALATION_REQUESTED: 'Escalation requested',
  CALLBACK_REQUESTED: 'Callback requested',
  TRANSFERRED: 'Connected to a colleague',
  UNKNOWN: 'Unclassified',
};

export const notificationSeverityLabels: Record<NotificationSeverity, string> = {
  INFO: 'Info',
  OK: 'Good',
  WARNING: 'Warning',
  CRITICAL: 'Critical',
};

export const exportFormatLabels: Record<ExportFormat, string> = {
  CSV: 'CSV',
  XLSX: 'Excel (XLSX)',
};

/**
 * How each activity row reads (S11).
 *
 * A verb phrase completing "<subject> …", so the feed composes as the mock does
 * — emphasised subject, plain remainder — without the server sending prose.
 */
export const activityKindLabels: Record<ActivityKind, string> = {
  CALL_COMPLETED: 'called',
  CALL_ABANDONED: 'hung up without speaking',
  CALL_FAILED: 'called, but the call could not be recorded',
  ESCALATION: 'needs a person',
  AGENT_UPDATED: 'was reconfigured',
};
